import { useApiQuery } from '../useApiQuery';
import { operationsClient, apiGet } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { BookingListParams, BookingListResponse } from '@/types/scheduler';

const MAX_BOOKINGS_PAGE_SIZE = 100;

export function useSchedulerBookings(
  params: BookingListParams = {},
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useApiQuery<BookingListResponse>({
    queryKey: schedulerKeys.bookings(params),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        schedulerEndpoints.bookings(),
        { params },
      );
      // API returns { items, total } but our type expects { data, total }
      const items = (raw.items ?? raw.data ?? []) as BookingListResponse['data'];
      return {
        data: items,
        total: Number(raw.total ?? items.length),
        page: Number(raw.page ?? 1),
        limit: Number(raw.limit ?? items.length),
      };
    },
    showErrorToast: false,
    enabled: options.enabled ?? true,
    // NOT-337 — the live cockpit polls this on the 30s tracking cadence so the
    // Today's-schedule statuses refresh without a manual reload.
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  });
}

export function useAllSchedulerBookings(
  params: BookingListParams = {},
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useApiQuery<BookingListResponse>({
    queryKey: schedulerKeys.bookings({ ...params, allPages: true }),
    queryFn: ({ signal }) => fetchAllSchedulerBookings(params, signal),
    enabled: options.enabled ?? true,
    showErrorToast: false,
    ...(options.refetchInterval ? { refetchInterval: options.refetchInterval } : {}),
  });
}

async function fetchAllSchedulerBookings(params: BookingListParams, signal: AbortSignal) {
  const data: BookingListResponse['data'] = [];
  let total = 0;
  let offset = 0;

  while (true) {
    const raw = await apiGet<Record<string, unknown>>(
      operationsClient,
      schedulerEndpoints.bookings(),
      {
        signal,
        params: {
          ...params,
          limit: MAX_BOOKINGS_PAGE_SIZE,
          offset,
        },
      },
    );
    const items = (raw.items ?? raw.data ?? []) as BookingListResponse['data'];
    data.push(...items);

    const rawTotal = Number(raw.total);
    const hasKnownTotal = Number.isFinite(rawTotal) && rawTotal >= 0;
    total = hasKnownTotal ? rawTotal : data.length;

    if (
      items.length === 0 ||
      items.length < MAX_BOOKINGS_PAGE_SIZE ||
      (hasKnownTotal && data.length >= total)
    ) {
      break;
    }

    offset += MAX_BOOKINGS_PAGE_SIZE;
  }

  return {
    data,
    total,
    page: 1,
    limit: data.length || MAX_BOOKINGS_PAGE_SIZE,
  };
}

/** The API's technician filter includes leads and helpers. Deduplicate shared visits. */
export function useTeamSchedulerBookings(
  params: BookingListParams,
  technicianIds: string[],
  options: { enabled?: boolean } = {},
) {
  const ids = [...new Set(technicianIds)].sort();
  return useApiQuery<BookingListResponse>({
    queryKey: schedulerKeys.bookings({ ...params, allPages: true, technicianIds: ids }),
    enabled: (options.enabled ?? true) && ids.length > 0,
    showErrorToast: false,
    queryFn: async ({ signal }) => {
      const unique = new Map<string, BookingListResponse['data'][number]>();
      // Bound concurrent requests even for a large team, without loading unrelated teams.
      for (let index = 0; index < ids.length; index += 4) {
        signal.throwIfAborted();
        const pages = await Promise.all(
          ids
            .slice(index, index + 4)
            .map((technicianId) => fetchAllSchedulerBookings({ ...params, technicianId }, signal)),
        );
        for (const page of pages) {
          for (const booking of page.data) unique.set(booking.bookingId, booking);
        }
      }
      const data = [...unique.values()];
      return { data, total: data.length, page: 1, limit: data.length || MAX_BOOKINGS_PAGE_SIZE };
    },
  });
}
